import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Activity, Flame, Sparkles, Snowflake, AlertCircle } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import {
  getEngageStatusBadge, ENGAGE_STATUS_LABEL,
} from '../../../utils/engageStageBadge';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { filterByRange } from './ReportFilters';


export default function EngagementDistributionReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [engagementByEnq, setEngagementByEnq] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/enquiries/');
        const list = resp?.results || resp?.data?.results || [];
        if (cancelled) return;
        setEnquiries(list);

        // For active enquiries, fetch engagement in parallel (throttled by browser)
        const activeSubset = list.filter(e => !['converted', 'dropped', 'lost'].includes(e.status)).slice(0, 30);
        const results = await Promise.all(activeSubset.map(async e => {
          try {
            const r = await apiFetch(`engage/analytics/patient-engagement/${e.id}/`);
            return [e.id, r?.data?.data || r?.data];
          } catch { return [e.id, null]; }
        }));
        if (cancelled) return;
        const map = {};
        for (const [id, d] of results) if (d) map[id] = d;
        setEngagementByEnq(map);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filteredEnquiries = useMemo(() => {
    let f = filterByRange(enquiries, preset, customFrom, customTo, 'created_date');
    if (stageFilter !== 'all') f = f.filter(e => e.status === stageFilter);
    return f;
  }, [enquiries, preset, customFrom, customTo, stageFilter]);

  const grouped = useMemo(() => {
    const g = { Hot: [], Warm: [], Cold: [] };
    const filteredIds = new Set(filteredEnquiries.map(e => String(e.id)));
    for (const eid of Object.keys(engagementByEnq)) {
      if (!filteredIds.has(String(eid))) continue;
      const d = engagementByEnq[eid];
      const enq = enquiries.find(e => String(e.id) === String(eid));
      if (!enq) continue;
      const gr = d?.grade || 'Cold';
      g[gr].push({ ...enq, _score: d?.score || 0, _alerts: d?.alerts || [] });
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => b._score - a._score);
    return g;
  }, [engagementByEnq, enquiries, filteredEnquiries]);

  const stageOptions = useMemo(() => [
    { value: 'all', label: 'All stages' },
    ...Object.entries(ENGAGE_STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })),
  ], []);

  const totals = { Hot: grouped.Hot.length, Warm: grouped.Warm.length, Cold: grouped.Cold.length };

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white shadow-sm">
          <Activity className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Engagement Distribution</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Hot / Warm / Cold patient distribution across active enquiries</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{ label: 'Stage', value: stageFilter, onChange: setStageFilter, options: stageOptions }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Flame className="w-4 h-4 text-red-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Hot</span></div><div className="text-xl font-bold mt-1 text-red-600 dark:text-red-400">{totals.Hot}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Warm</span></div><div className="text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">{totals.Warm}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Snowflake className="w-4 h-4 text-slate-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Cold</span></div><div className="text-xl font-bold mt-1 text-slate-600 dark:text-slate-400">{totals.Cold}</div></div>
      </div>

      {loading && Object.keys(engagementByEnq).length === 0 && <div className="p-4 text-sm text-gray-500">Computing engagement scores…</div>}

      {['Hot', 'Warm', 'Cold'].map(grade => (
        grouped[grade].length === 0 ? null : (
          <div key={grade} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                {grade === 'Hot' && <Flame className="w-4 h-4 text-red-500" />}
                {grade === 'Warm' && <Sparkles className="w-4 h-4 text-amber-500" />}
                {grade === 'Cold' && <Snowflake className="w-4 h-4 text-slate-500" />}
                {grade} patients
              </h2>
              <span className="text-[11px] text-gray-500">{grouped[grade].length}</span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {grouped[grade].slice(0, 10).map(row => (
                <Link key={row.id} to={`/engage/enquiries/${row.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(row.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{row.name}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{row.department_of_interest || '—'}</div>
                  </div>
                  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(row.status))}>{ENGAGE_STATUS_LABEL[row.status] || row.status}</span>
                  <div className="text-sm font-bold text-gray-900 dark:text-white w-10 text-right">{Math.round(row._score)}</div>
                  {row._alerts?.length > 0 && (
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" title={row._alerts.map(a => a.message).join('; ')} />
                  )}
                </Link>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
}
